import SVLink from '@pkgs/shared-client/components/SVLink';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import UpgradeButtonSequencePNG from '@pkgs/shared-client/img/upgrade-button-sequence.png';
import UpgradeButtonStaticPNG from '@pkgs/shared-client/img/upgrade-button-static.png';
import objectKeys from '@pkgs/shared/helpers/objectKeys';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

const ANIMATION_PHASES = {
	static: 3000,
	'fade-in': 700,
	animate: Math.round((28 / 30) * 1000), // 28 frames at 30fps
	'fade-out': 700,
} as const;

const ANIMATION_PHASES_KEYS = objectKeys(ANIMATION_PHASES);

export default function SVAnimatedUpgradeButton({
	className,
	onClick,
	to = '/upgrade/',
}: React.PropsWithChildren<{
	className?: string;
	onClick?: (event: React.UIEvent) => void;
	to?: string;
}>) {
	const [animationPhase, setAnimationPhase] = useState<keyof typeof ANIMATION_PHASES>('animate');

	useEffect(() => {
		const timeout = setTimeout(() => {
			const currentPhaseIndex = ANIMATION_PHASES_KEYS.indexOf(animationPhase);
			const nextPhaseIndex = (currentPhaseIndex + 1) % ANIMATION_PHASES_KEYS.length;
			const nextPhase = ANIMATION_PHASES_KEYS[nextPhaseIndex];

			setAnimationPhase(nextPhase);
		}, ANIMATION_PHASES[animationPhase]);

		return () => {
			clearTimeout(timeout);
		};
	}, [animationPhase]);

	const handleMouseOver = useEventCallback(() => {
		setAnimationPhase('animate');
	});

	return (
		<div className={twMerge('group relative inline-block h-[48px] w-[130px]', className)}>
			<div className="bg-primary absolute inset-0 top-0 scale-[65%] rounded-full opacity-0 blur-md transition-all duration-[1s] ease-in-out group-hover:top-1 group-hover:scale-[85%] group-hover:opacity-50" />
			<SVLink
				className={twMerge(
					'upgrade-button-animation relative inline-block h-[48px] w-[130px] cursor-pointer',
					animationPhase !== 'animate' && 'upgrade-button-animation-stopped',
					animationPhase === 'static' && 'opacity-0',
				)}
				onClick={onClick}
				onMouseOver={handleMouseOver}
				style={{ backgroundImage: `url(${UpgradeButtonSequencePNG})` }}
				to={to}
			>
				<span className="sr-only">Upgrade</span>
			</SVLink>
			<div
				className={twMerge(
					'pointer-events-none absolute inset-0 bg-contain bg-no-repeat opacity-100 transition-opacity duration-1000',
					(animationPhase === 'fade-in' || animationPhase === 'animate') && 'opacity-0',
				)}
				style={{
					backgroundImage: `url(${UpgradeButtonStaticPNG})`,
				}}
			/>
		</div>
	);
}
